import { useQueries } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Boxes,
  GitBranch,
  Lightbulb,
  Plus,
  Radio,
} from "lucide-react";
import { useMemo } from "react";

import { DashboardShell } from "@/components/dravian/dashboard-shell";
import { Eyebrow } from "@/components/dravian/eyebrow";
import { RiskPill } from "@/components/dravian/risk-pill";
import { WorkspaceCardMenu } from "@/components/dravian/workspace-card-menu";
import { entities as entitiesApi, signals as signalsApi, scenarios as scenariosApi, type ApiEntity, type ApiSignal, type ApiScenario } from "@/lib/api";
import { colorForType } from "@/lib/entity-color";
import { useWorkspaces } from "@/store/workspaces";

export const Route = createFileRoute("/hq")({
  head: () => ({
    meta: [
      { title: "Dashboard — Dravian Horizon" },
      {
        name: "description",
        content: "Operational risk intelligence overview across your workspaces.",
      },
    ],
  }),
  component: Index,
});

interface AggSignal extends ApiSignal {
  workspaceId: string;
  workspaceName: string;
}

interface AggEntity extends ApiEntity {
  workspaceId: string;
  workspaceName: string;
  signalCount: number;
}

function Index() {
  const { workspaces } = useWorkspaces();

  // Parallel fetches across every workspace for signals + entities + scenarios.
  // Per-workspace results stay in cache so the workspace pages don't re-fetch.
  const signalsQ = useQueries({
    queries: workspaces.map((w) => ({
      queryKey: ["signals", w.id],
      queryFn: () => signalsApi.list(w.id),
    })),
  });
  const entitiesQ = useQueries({
    queries: workspaces.map((w) => ({
      queryKey: ["entities", w.id],
      queryFn: () => entitiesApi.list(w.id),
    })),
  });
  const scenariosQ = useQueries({
    queries: workspaces.map((w) => ({
      queryKey: ["scenarios", w.id],
      queryFn: () => scenariosApi.list(w.id),
    })),
  });

  const allSignals: AggSignal[] = useMemo(() => {
    const out: AggSignal[] = [];
    workspaces.forEach((w, i) => {
      (signalsQ[i]?.data ?? []).forEach((s) =>
        out.push({ ...s, workspaceId: w.id, workspaceName: w.name }),
      );
    });
    return out;
  }, [workspaces, signalsQ]);

  const allEntities: AggEntity[] = useMemo(() => {
    const signalsByEntity = new Map<string, number>();
    for (const s of allSignals) {
      for (const eid of s.related_entity_ids) {
        signalsByEntity.set(eid, (signalsByEntity.get(eid) ?? 0) + 1);
      }
    }
    const out: AggEntity[] = [];
    workspaces.forEach((w, i) => {
      (entitiesQ[i]?.data ?? []).forEach((e) =>
        out.push({
          ...e,
          workspaceId: w.id,
          workspaceName: w.name,
          signalCount: signalsByEntity.get(e.id) ?? 0,
        }),
      );
    });
    return out;
  }, [workspaces, entitiesQ, allSignals]);

  const allScenarios: ApiScenario[] = useMemo(() => {
    const out: ApiScenario[] = [];
    scenariosQ.forEach((q) => (q.data ?? []).forEach((s) => out.push(s)));
    return out;
  }, [scenariosQ]);

  // Recent signals: last 24h count + top 8 most recent for the feed
  const cutoff24h = Date.now() - 24 * 3600_000;
  const cutoff7d = Date.now() - 7 * 86400_000;
  const signals24h = allSignals.filter(
    (s) => new Date(s.timestamp).getTime() >= cutoff24h,
  ).length;
  const predictions = allSignals.filter((s) => s.type === "prediction");
  const scenariosThisWeek = allScenarios.filter(
    (s) => new Date(s.created_at).getTime() >= cutoff7d,
  ).length;

  const recentSignalsFeed = [...allSignals]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 8);

  // Top entities = most signals attached
  const topEntities = [...allEntities]
    .sort((a, b) => b.signalCount - a.signalCount)
    .slice(0, 5);

  const totalEntities = allEntities.length;
  const totalSignals = allSignals.length;

  const stats = [
    {
      label: "Workspaces",
      value: workspaces.length,
      sub: `${workspaces.filter((w) => w.ontology_id).length} with ontology`,
      icon: Boxes,
    },
    {
      label: "Signals (24h)",
      value: signals24h,
      sub: `${totalSignals} total`,
      icon: Radio,
    },
    {
      label: "Entities tracked",
      value: totalEntities,
      sub: `across ${workspaces.length} workspaces`,
      icon: Lightbulb,
    },
    {
      label: "Scenarios (7d)",
      value: scenariosThisWeek,
      sub: `${allScenarios.length} total · ${predictions.length} hypotheses`,
      icon: GitBranch,
    },
  ];

  const anyLoading = signalsQ.some((q) => q.isLoading) || entitiesQ.some((q) => q.isLoading);

  return (
    <DashboardShell>
      <main className="flex-1 mx-auto w-full max-w-[1400px] px-6 py-8 space-y-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow mb-1">Overview</div>
            <h1 className="text-[28px] font-semibold tracking-tight">
              Operational Risk at a Glance
            </h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              All workspaces, signals, and scenarios across your deployment.
            </p>
          </div>
          <Link
            to="/workspaces/new"
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-[13px] font-medium hover:bg-primary/90 glow-primary"
          >
            <Plus className="h-4 w-4" /> New Workspace
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="panel p-4">
                <div className="flex items-start justify-between">
                  <span className="label-eyebrow">{s.label}</span>
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-3 text-[32px] font-semibold tracking-tight">
                  {anyLoading ? "…" : s.value}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">{s.sub}</div>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <section className="panel p-5 lg:col-span-2">
            <Eyebrow
              count={workspaces.length}
              right={
                <Link to="/workspaces" className="text-[11px] text-primary hover:underline">
                  View all
                </Link>
              }
            >
              Workspaces
            </Eyebrow>
            {workspaces.length === 0 ? (
              <div className="text-center py-8 text-[12px] text-muted-foreground">
                You haven't created any workspaces yet.
                <div className="mt-3">
                  <Link
                    to="/workspaces/new"
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-medium"
                  >
                    <Plus className="h-3.5 w-3.5" /> Create one
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {workspaces.map((w) => (
                  <Link
                    key={w.id}
                    to="/$workspace/geographic"
                    params={{ workspace: w.id }}
                    className="panel-elevated p-4 hover:border-primary/40 transition group relative"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold truncate">{w.name}</div>
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
                          {w.domain.replace("_", " ")}
                          {w.ontology_id && " · with ontology"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <RiskPill level={w.risk} />
                        <WorkspaceCardMenu workspace={w} />
                      </div>
                    </div>
                    {w.description && (
                      <p className="text-[12px] text-muted-foreground mt-2 line-clamp-2">
                        {w.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                      <div className="flex gap-3 text-[11px] text-muted-foreground font-mono">
                        <span>{w.entities} ent</span>
                        <span>{w.signals} sig</span>
                        <span>{w.updatedAt}</span>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition" />
                    </div>
                  </Link>
                ))}
                <Link
                  to="/workspaces/new"
                  className="panel-elevated p-4 border-dashed flex flex-col items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 transition min-h-[120px]"
                >
                  <Plus className="h-5 w-5 mb-1" />
                  <span className="text-[12px]">New Workspace</span>
                </Link>
              </div>
            )}
          </section>

          <section className="panel p-5">
            <Eyebrow count={topEntities.length}>Most-signalled entities</Eyebrow>
            {topEntities.length === 0 ? (
              <p className="text-[12px] text-muted-foreground py-4">
                Entities will appear here once your workspaces have signals attached.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {topEntities.map((e, i) => (
                  <li key={e.id} className="py-2.5 flex items-center gap-3">
                    <span className="text-[11px] text-muted-foreground font-mono w-4">
                      {i + 1}
                    </span>
                    <span
                      className="h-1.5 w-1.5 rounded-full shrink-0"
                      style={{ background: colorForType(e.type) }}
                    />
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/$workspace/entities"
                        params={{ workspace: e.workspaceId }}
                        className="text-[13px] truncate hover:text-primary block"
                      >
                        {e.name}
                      </Link>
                      <div className="text-[10px] text-muted-foreground capitalize">
                        {e.type.replace(/_/g, " ")} · {e.workspaceName}
                      </div>
                    </div>
                    <span className="text-[12px] font-mono text-foreground/80 w-12 text-right">
                      {e.signalCount} sig
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <section className="panel p-5">
          <Eyebrow
            count={recentSignalsFeed.length}
            right={<span className="text-[11px] text-muted-foreground">across all workspaces</span>}
          >
            Recent Signals
          </Eyebrow>
          {recentSignalsFeed.length === 0 ? (
            <p className="text-[12px] text-muted-foreground py-4">
              No signals ingested yet — add them via chat, RSS, or CSV upload inside a workspace.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {recentSignalsFeed.map((s) => {
                const a = (s.attributes ?? {}) as Record<string, unknown>;
                const headline = String(
                  a.title ?? a.claim ?? a.headline ?? a.description ?? s.type,
                );
                const isPrediction = s.type === "prediction";
                return (
                  <li key={s.id} className="py-3 flex items-center gap-4">
                    <span
                      className={`h-2 w-2 rounded-full shrink-0 ${
                        isPrediction ? "bg-[#a855f7]" : "bg-primary"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <Link
                        to="/$workspace/signals"
                        params={{ workspace: s.workspaceId }}
                        className="text-[13px] truncate hover:text-primary block"
                      >
                        {headline}
                      </Link>
                      <div className="text-[11px] text-muted-foreground">
                        <span className="font-mono">{s.type}</span> · {s.source} ·{" "}
                        {s.workspaceName}
                      </div>
                    </div>
                    <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">
                      {relative(s.timestamp)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </DashboardShell>
  );
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}
